export const RECENT_KEY = 'utm_recent_links'

const CYRILLIC_MAP = {
  'а': 'a', 'б': 'b', 'в': 'v', 'г': 'g', 'д': 'd', 'е': 'e', 'ё': 'e',
  'ж': 'zh', 'з': 'z', 'и': 'i', 'й': 'y', 'к': 'k', 'л': 'l', 'м': 'm',
  'н': 'n', 'о': 'o', 'п': 'p', 'р': 'r', 'с': 's', 'т': 't', 'у': 'u',
  'ф': 'f', 'х': 'h', 'ц': 'c', 'ч': 'ch', 'ш': 'sh', 'щ': 'sch',
  'ъ': '', 'ы': 'y', 'ь': '', 'э': 'e', 'ю': 'yu', 'я': 'ya',
  'А': 'A', 'Б': 'B', 'В': 'V', 'Г': 'G', 'Д': 'D', 'Е': 'E', 'Ё': 'E',
  'Ж': 'Zh', 'З': 'Z', 'И': 'I', 'Й': 'Y', 'К': 'K', 'Л': 'L', 'М': 'M',
  'Н': 'N', 'О': 'O', 'П': 'P', 'Р': 'R', 'С': 'S', 'Т': 'T', 'У': 'U',
  'Ф': 'F', 'Х': 'H', 'Ц': 'C', 'Ч': 'Ch', 'Ш': 'Sh', 'Щ': 'Sch',
  'Ъ': '', 'Ы': 'Y', 'Ь': '', 'Э': 'E', 'Ю': 'Yu', 'Я': 'Ya', ' ': '_'
}

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']

export function transliterate(text) {
  return String(text ?? '').replace(/[а-яА-ЯёЁ\s]/g, (char) => CYRILLIC_MAP[char] || char)
}

export function hasCyrillic(text) {
  return /[а-яА-ЯёЁ]/.test(String(text ?? ''))
}

/** Значение события: строка с точкой — плейсхолдер, в ссылку не пишется. */
export function valueFromEvent(raw) {
  if (raw == null) return ''
  const text = String(raw)
  if (!text || text.startsWith('.')) return ''
  return text
}

export function normalizeUrlInput(raw, protocol = 'https://') {
  let value = String(raw ?? '').trim()
  let nextProtocol = protocol === 'http://' ? 'http://' : 'https://'
  const match = value.match(/^(https?:\/\/)/i)
  if (match) {
    nextProtocol = match[1].toLowerCase() === 'http://' ? 'http://' : 'https://'
    value = value.slice(match[0].length)
  }
  value = value.replace(/\s+/g, '').replace(/[?&]+$/, '')
  return { protocol: nextProtocol, value }
}

export function validateUrl(path) {
  const value = String(path ?? '').trim()
  if (!value || !value.includes('.') || /\s/.test(value)) return false
  return true
}

/** Пустая строка — ещё не адрес. Непустая без точки или с пробелами — опечатка. */
export function addressIssue(raw) {
  const value = String(raw ?? '').trim().replace(/^(https?:\/\/)/i, '')
  if (!value) return ''
  if (/\s/.test(value) || !value.includes('.')) return 'Похоже на опечатку в адресе'
  return ''
}

export function formatTermDate(date) {
  if (!date) return ''
  const parsed = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(parsed.getTime())) return ''
  const dd = String(parsed.getDate()).padStart(2, '0')
  const mm = String(parsed.getMonth() + 1).padStart(2, '0')
  return `${dd}${mm}${parsed.getFullYear()}`
}

export function parseTermDate(raw) {
  const text = String(raw ?? '').trim()
  if (!/^\d{8}$/.test(text)) return null
  const day = Number(text.slice(0, 2))
  const month = Number(text.slice(2, 4))
  const year = Number(text.slice(4))
  const date = new Date(year, month - 1, day)
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null
  return date
}

export function formatUrlForDisplay(url, max = 72) {
  if (!url) return ''
  const stripped = String(url).replace(/^https?:\/\//, '')
  if (stripped.length <= max) return stripped
  return `${stripped.slice(0, max - 1)}…`
}

export function formatRecentLabel(ts) {
  const date = new Date(ts)
  if (Number.isNaN(date.getTime())) return ''
  const start = (value) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()
  const diff = Math.round((start(new Date()) - start(date)) / 86400000)
  if (diff === 0) return 'сегодня'
  if (diff === 1) return 'вчера'
  return `${String(date.getDate()).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}`
}

export function readRecent() {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]')
    if (!Array.isArray(raw)) return []
    return raw.filter((item) => item && item.url).slice(0, 10)
  } catch {
    return []
  }
}

export function pushRecent(url) {
  const list = readRecent().filter((item) => item.url !== url)
  list.unshift({ url, ts: Date.now() })
  const next = list.slice(0, 10)
  localStorage.setItem(RECENT_KEY, JSON.stringify(next))
  return next
}

export function clearRecent() {
  localStorage.removeItem(RECENT_KEY)
}

export function mergePopular(origins, serverRows, limit = 20) {
  const parsed = Number(limit)
  const cap = Number.isFinite(parsed) ? Math.max(0, Math.trunc(parsed)) : 20
  const usesById = new Map()
  ;(Array.isArray(serverRows) ? serverRows : []).forEach((row) => {
    if (!row || row.id == null) return
    usesById.set(row.id, Number(row.uses) || 0)
  })
  const list = (Array.isArray(origins) ? origins : []).map((row) => {
    const uses = usesById.get(row.id) || 0
    return { ...row, uses, score: uses }
  })
  const byName = (a, b) => String(a.event ?? '').localeCompare(String(b.event ?? ''), 'ru')
  const scored = list
    .filter((row) => row.score > 0)
    .sort((a, b) => b.score - a.score || byName(a, b))
  const picked = []
  const seen = new Set()
  scored.forEach((row) => {
    if (picked.length >= cap || seen.has(row.id)) return
    seen.add(row.id)
    picked.push(row)
  })
  if (picked.length < cap) {
    const rest = list
      .filter((row) => !seen.has(row.id))
      .sort(byName)
    rest.forEach((row) => {
      if (picked.length >= cap || seen.has(row.id)) return
      seen.add(row.id)
      picked.push(row)
    })
  }
  return picked
}

/**
 * Единственная сборка ссылки.
 * fields — уже разрешённые значения (плейсхолдеры `.…` сюда не попадают).
 */
export function buildUtmUrl({ protocol = 'https://', path = '', fields = {}, fromEvent = {} } = {}) {
  const normalized = normalizeUrlInput(path, protocol)
  const params = []
  UTM_KEYS.forEach((key) => {
    let value = fields[key]
    if (value == null || value === '' || value === 'none') return
    value = String(value)
    if (hasCyrillic(value)) value = transliterate(value)
    if (value === '' || value === 'none') return
    params.push({ key, value, fromEvent: Boolean(fromEvent[key]) })
  })

  if (!normalized.value) return { url: '', params, base: '' }

  const base = `${normalized.protocol}${normalized.value}`
  if (!params.length) return { url: base, params, base }

  const joiner = base.includes('?') ? '&' : '?'
  const query = params.map((item) => `${item.key}=${item.value}`).join('&')
  return { url: `${base}${joiner}${query}`, params, base }
}

export function csvLine(cells) {
  return cells.map((cell) => {
    const text = cell == null ? '' : String(cell)
    if (/[";\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`
    return text
  }).join(';')
}

export function downloadCsv(filename, header, rows) {
  const body = [csvLine(header), ...rows.map((row) => csvLine(row))].join('\r\n')
  const blob = new Blob([`\uFEFF${body}`], { type: 'text/csv;charset=utf-8' })
  const link = document.createElement('a')
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  URL.revokeObjectURL(link.href)
}
